/* eslint-disable no-param-reassign */
import { Applicant, defaultApplicant } from '@common/entities';
import { ApplicantApplicationResponse } from '@common/entities/src/applicant-application-response.entity';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { resetStoreAction } from 'state/commonActions';

import type { Form } from 'types/form';
import { getActiveResponse } from 'utils/getActiveResponse';

export type ApplicationReducer = {
  applicant: Applicant;
  form: Form;
  activeResponse: ApplicantApplicationResponse;
};

export const defaultFormState: Form = {
  id: '',
  name: '',
  description: '',
  type: '',
  clientId: 0,
  pages: [],
  occupancyType: '',
  versionId: '',
  applicationId: '',
  settings: {},
};

export const defaultActiveResponse: ApplicantApplicationResponse = {
  responseId: '',
  versionId: '',
  applicationId: '',
  entrataApplicationId: 0,
  leaseId: 0,
  leaseIntervalId: 0,
};

const initialState: ApplicationReducer = {
  applicant: {
    ...defaultApplicant,
    responses: defaultApplicant.responses || [],
  },
  form: defaultFormState,
  activeResponse: defaultActiveResponse,
};

const applicationSlice = createSlice({
  name: 'application',
  initialState,
  reducers: {
    setApplicationData: (
      state,
      action: PayloadAction<{
        applicant: Applicant;
        form: Form;
        activeResponseId?: string;
      }>,
    ) => {
      const { applicant, form, activeResponseId } = action.payload;
      if (activeResponseId) {
        const activeResponse = getActiveResponse(applicant, activeResponseId);
        return {
          ...state,
          applicant,
          form,
          activeResponse: {
            ...activeResponse,
            paymentProcessed:
              activeResponse.responseId === state.activeResponse.responseId &&
              state.activeResponse.paymentProcessed === true
                ? true
                : activeResponse.paymentProcessed,
          },
        };
      }
      return { ...state, applicant, form };
    },
    setActiveResponse: (state, action: PayloadAction<string>) => {
      const activeResponse = getActiveResponse(state.applicant, action.payload);
      state.activeResponse = {
        ...activeResponse,
        paymentProcessed:
          activeResponse.responseId === state.activeResponse.responseId &&
          state.activeResponse.paymentProcessed === true
            ? true
            : activeResponse.paymentProcessed,
      };
    },
    markActiveResponsePaymentProcessed: (state, action: PayloadAction<string>) => {
      if (state.activeResponse.responseId === action.payload) {
        state.activeResponse.paymentProcessed = true;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(resetStoreAction, () => initialState);
  },
});

export const { setApplicationData, setActiveResponse, markActiveResponsePaymentProcessed } =
  applicationSlice.actions;
export default applicationSlice.reducer;
